import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Button, Grid, useMediaQuery, useTheme, Divider } from '@mui/material';
import Paper from '@mui/material/Paper';
import { CreateDiscountImage, ZeroResultsImage } from '@/resources/images';
import {
    FilterButton,
    TableCard,
    TableMaker,
    TableMobileLabel,
    PageEmptyState,
    Pagination,
    PageTitle,
    SearchInput,
    SEO,
    SortButton,
    ListingClearButton,
    WrapWithPaper,
    TableSkeleton
} from '@/components/common';
import { DiscountFilterDrawer } from '@/components/FilterDrawers';
import { taxDiscountSortMenuItems } from '@/config';
import {
    useCheckHasBusinessLoaded,
    useCurrency,
    useLocale,
    useListingState,
    useUrlParams
} from '@/hooks';
import { DISCOUNT_BASE_PARAM } from '@/constants';
import { getPaginatedDiscounts } from '@/modules/dataWrangler/dataWrangler';
import { openDrawer, fetchListingItems } from '@/state/actions';
import { Container } from '@/containers';
import { DiscountActive } from '@/resources/icons';
import tableHead from './tableHead';
import { dataProcessor, generateComponents } from './dataProcessors';
import useStyles from '../styles';

const Discounts = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const { code: currency } = useCurrency();
    const locale = useLocale();
    const hasBusinessLoaded = useCheckHasBusinessLoaded();
    const { setUrlParams, getUrlParams } = useUrlParams();

    const {
        hasData,
        items,
        loadingItems,
        loadingPaper,
        totalPages,
        zeroResultsReturned
    } = useListingState('discount');

    const classes = useStyles({ hasData });

    const dispatch = useDispatch();

    const currentUrlParams = getUrlParams({ base: DISCOUNT_BASE_PARAM });
    const stringifiedParams = JSON.stringify(currentUrlParams);

    const setOrder = (property) => {
        const sortOrder =
            currentUrlParams?.sort?.type === property &&
            currentUrlParams?.sort?.order === 'asc'
                ? 'desc'
                : 'asc';

        const newUrlParams = {
            ...currentUrlParams,
            sort: {
                type: property,
                order: sortOrder
            }
        };

        setUrlParams({ base: DISCOUNT_BASE_PARAM, params: newUrlParams });
    };

    useEffect(() => {
        if (hasBusinessLoaded) {
            getPaginatedDiscounts(JSON.parse(stringifiedParams));
        }
    }, [hasBusinessLoaded, stringifiedParams]);

    return (
        <>
            <SEO title="All Discounts" />
            <Container>
                {!hasData && !loadingItems && (
                    <PageEmptyState
                        image={<CreateDiscountImage />}
                        content="Create Your First Discount Item"
                        buttonText="Create Discount"
                        onClick={() => {
                            dispatch(openDrawer('discount'));
                        }}
                    />
                )}
                {hasData && !loadingPaper && (
                    <>
                        {!isMobile && (
                            <Paper elevation={0} variant="outlined">
                                <Grid
                                    container
                                    direction="row"
                                    spacing={1}
                                    wrap="nowrap"
                                    alignItems="center"
                                >
                                    <Grid item md={2}>
                                        <PageTitle
                                            icon={<DiscountActive />}
                                            title="Discounts"
                                        />
                                    </Grid>
                                    <Grid item md={4}>
                                        <div className={classes.searchWrap}>
                                            <SearchInput
                                                base={DISCOUNT_BASE_PARAM}
                                                id="dropdownSearch"
                                                label="Search"
                                                ariaLabel="Search"
                                            />
                                        </div>
                                    </Grid>

                                    <Grid
                                        item
                                        md={2}
                                        className={classes.filterButtonWrap}
                                    >
                                        <Grid container alignItems="center">
                                            <Grid item md={8}>
                                                <FilterButton
                                                    base={DISCOUNT_BASE_PARAM}
                                                    type="discount"
                                                    watch={[
                                                        'value',
                                                        'type',
                                                        'tagIds'
                                                    ]}
                                                />
                                            </Grid>
                                            <Grid item md={4}>
                                                <ListingClearButton
                                                    base={DISCOUNT_BASE_PARAM}
                                                    watch={[
                                                        's',
                                                        'value',
                                                        'type',
                                                        'tagIds'
                                                    ]}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Grid>

                                    <Grid
                                        item
                                        container
                                        justifyContent="flex-end"
                                        md={4}
                                        className={classes.newButtonContainer}
                                    >
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            fullWidth
                                            onClick={() =>
                                                dispatch(openDrawer('discount'))
                                            }
                                            className={classes.newButton}
                                        >
                                            New Discount
                                        </Button>
                                    </Grid>
                                </Grid>

                                <Divider />

                                <TableMaker
                                    actionCols={[4, 5, 6]}
                                    ariaTableName="Discounts Table"
                                    handleRequestSort={setOrder}
                                    headCells={tableHead}
                                    rowData={
                                        hasData && !zeroResultsReturned
                                            ? generateComponents({
                                                  array: dataProcessor(items),
                                                  currency,
                                                  classes,
                                                  locale
                                              })
                                            : []
                                    }
                                    order={
                                        currentUrlParams?.sort?.order || 'asc'
                                    }
                                    orderBy={currentUrlParams?.sort?.type || ''}
                                />
                                {zeroResultsReturned && (
                                    <PageEmptyState
                                        image={<ZeroResultsImage />}
                                        content="The selected filters returned 0 results."
                                        buttonText="Reset filters"
                                        onClick={() => {
                                            dispatch(
                                                fetchListingItems({
                                                    type: 'discount'
                                                })
                                            );
                                            setUrlParams({
                                                base: DISCOUNT_BASE_PARAM,
                                                params: null
                                            });
                                        }}
                                        hidePaper
                                    />
                                )}
                                <Pagination
                                    base={DISCOUNT_BASE_PARAM}
                                    totalPages={totalPages}
                                />
                            </Paper>
                        )}

                        {isMobile && (
                            <>
                                <Grid
                                    container
                                    direction="row"
                                    spacing={1}
                                    wrap="nowrap"
                                    alignItems="center"
                                >
                                    <Grid item xs={6}>
                                        <PageTitle
                                            icon={<DiscountActive />}
                                            title="Discounts"
                                        />
                                    </Grid>

                                    <Grid
                                        item
                                        container
                                        justifyContent="flex-end"
                                        xs={6}
                                        className={classes.newButtonContainer}
                                    >
                                        <Grid
                                            item
                                            xs={12}
                                            className={classes.newButton}
                                        >
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                fullWidth
                                                onClick={() =>
                                                    dispatch(
                                                        openDrawer('discount')
                                                    )
                                                }
                                            >
                                                New Discount
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid container>
                                    <Grid item xs={12}>
                                        <div className={classes.searchWrap}>
                                            <SearchInput
                                                base={DISCOUNT_BASE_PARAM}
                                                id="dropdownSearch"
                                                label="Search"
                                                ariaLabel="Search"
                                            />
                                        </div>
                                    </Grid>
                                </Grid>
                                <Grid container justifyContent="center">
                                    <Grid
                                        item
                                        container
                                        spacing={1}
                                        xs={12}
                                        direction="row"
                                        wrap="nowrap"
                                        alignItems="center"
                                        justifyContent="center"
                                    >
                                        <Grid
                                            item
                                            container
                                            xs={6}
                                            className={classes.sortButtonWrap}
                                        >
                                            <SortButton
                                                base={DISCOUNT_BASE_PARAM}
                                                isLoading={loadingItems}
                                                sortMenuItems={
                                                    taxDiscountSortMenuItems
                                                }
                                            />
                                        </Grid>
                                        <Grid
                                            item
                                            xs={6}
                                            className={classes.filterButtonWrap}
                                        >
                                            <FilterButton
                                                base={DISCOUNT_BASE_PARAM}
                                                type="discount"
                                                watch={[
                                                    'value',
                                                    'type',
                                                    'tagIds'
                                                ]}
                                            />
                                        </Grid>
                                    </Grid>
                                    <Grid item md={4}>
                                        <ListingClearButton
                                            base={DISCOUNT_BASE_PARAM}
                                            watch={[
                                                's',
                                                'value',
                                                'type',
                                                'sort',
                                                'tagIds'
                                            ]}
                                            hide={
                                                zeroResultsReturned ||
                                                loadingItems
                                            }
                                        />
                                    </Grid>
                                </Grid>
                                {hasData &&
                                    !zeroResultsReturned &&
                                    generateComponents({
                                        array: dataProcessor(items),
                                        currency,
                                        classes,
                                        locale,
                                        isMobile
                                    }).map((item) => (
                                        <TableCard
                                            key={item.id}
                                            title={item.discountName}
                                            onClick={item.primaryClick}
                                            right={
                                                <>
                                                    <TableMobileLabel label="Amount" />
                                                    <span>{item.amount}</span>
                                                </>
                                            }
                                            left={
                                                <>
                                                    <TableMobileLabel label="Type" />
                                                    <span>{item.type}</span>
                                                </>
                                            }
                                            actions={item.actions}
                                            tags={item.tags}
                                        />
                                    ))}
                                {zeroResultsReturned && (
                                    <PageEmptyState
                                        image={<ZeroResultsImage />}
                                        content="The selected filters returned 0 results."
                                        buttonText="Reset filters"
                                        onClick={() => {
                                            dispatch(
                                                fetchListingItems({
                                                    type: 'discount'
                                                })
                                            );
                                            setUrlParams({
                                                base: DISCOUNT_BASE_PARAM,
                                                params: null
                                            });
                                        }}
                                    />
                                )}
                                {totalPages > 1 && (
                                    <Paper elevation={0} variant="outlined">
                                        <Pagination
                                            base={DISCOUNT_BASE_PARAM}
                                            totalPages={totalPages}
                                        />
                                    </Paper>
                                )}
                            </>
                        )}
                    </>
                )}
                {loadingPaper && (
                    <WrapWithPaper removeWrap={isMobile}>
                        <TableSkeleton isMobile={isMobile} />
                    </WrapWithPaper>
                )}
                {!loadingPaper && <DiscountFilterDrawer />}
            </Container>
        </>
    );
};

export default Discounts;
